import type { Translation } from "../../../../../hooks/useTranslations";
import type { OperationMode } from "../../../../../http/lock_calculator/types";
import { formatCoefficient } from "../../model/coefficientFormat";
import { COEFFICIENT_VALUE_PARAMS, type NoticeParams } from "../../model/noticeParams";
import { apiFieldOfPath, type CalculatorNotice } from "../../model/notices";
import { headerOrientationLabels, lateralResistanceLabels } from "../../model/structuralLabels";

type NoticesText = Translation["lockCalculatorModule"]["notices"];

const formatNumber = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);

/** Params that carry a lateral resistance configuration id, shown by its label. */
const LATERAL_CONFIG_PARAMS = ["fromConfig", "toConfig"];

/** Turns raw notice params into display strings: numbers rounded, enum values and input keys translated. */
const formatParams = (params: Record<string, unknown>, t: Translation): Record<string, string> => {
  const text: NoticesText = t.lockCalculatorModule.notices;
  const lateralConfigs = lateralResistanceLabels(t);
  const dictionaries: Record<string, Record<string, string>> = {
    axis: text.axes,
    member: text.members,
    reason: text.reasons,
    coefficient: text.coefficients,
    slot: text.slots,
    orientation: headerOrientationLabels(t),
  };
  const inputs: Record<string, string> = text.inputs;
  const formatted: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // A missing input is a backend key or, for the manual solution, a form path.
      const label = (item: unknown) => inputs[String(item)] ?? inputs[apiFieldOfPath(String(item))] ?? String(item);
      formatted[key] = value.map((item) => (key === "missing" ? label(item) : String(item))).join(", ");
    } else if (
      typeof value === "number" &&
      "coefficient" in params &&
      (COEFFICIENT_VALUE_PARAMS as readonly string[]).includes(key)
    ) {
      formatted[key] = formatCoefficient(value);
    } else if (typeof value === "number") {
      formatted[key] = LATERAL_CONFIG_PARAMS.includes(key)
        ? (lateralConfigs[value] ?? formatNumber(value))
        : formatNumber(value);
    } else if (typeof value === "string") {
      formatted[key] = dictionaries[key]?.[value] ?? value;
    } else {
      formatted[key] = value == null ? "—" : String(value);
    }
  });
  return formatted;
};

/** The label of every button a message can name, read from the translations the buttons themselves use. */
const buttonLabels = (t: Translation): Record<OperationMode | "generateReport" | "reportTab", string> => {
  const module = t.lockCalculatorModule;
  return {
    generateAll: module.structural.generateAllConfigurations,
    generateFromData: module.connector.generateFromData,
    generateManual: module.connector.generateManual,
    generateReport: module.report.generate,
    reportTab: module.report.title,
  };
};

export const noticeText = (notice: CalculatorNotice, t: Translation): string => {
  const text = t.lockCalculatorModule.notices;
  const message = (text.messages as Record<string, ((params: NoticeParams) => string) | undefined>)[notice.code];
  const params = formatParams(notice.params, t);
  const labels = buttonLabels(t);
  Object.assign(params, labels);
  if (notice.action) params["button"] = labels[notice.action];
  // A message only reads the params its notice carries.
  return message ? message(params as NoticeParams) : notice.code;
};

/** The text a field shows for its notices: the most severe, plus how many more there are. */
export const fieldNoticeText = (notices: CalculatorNotice[], t: Translation): string | undefined => {
  const [first] = notices;
  if (!first) return undefined;
  const extra = notices.length > 1 ? ` (+${notices.length - 1})` : "";
  return `${noticeText(first, t)}${extra}`;
};
